import React, { useEffect, useRef } from 'react';
import { Phone, Truck, Factory, Wrench, Package, Recycle, Construction, Container } from 'lucide-react';
import { Button } from './ui/button';
import { gsap } from 'gsap';

const equipmentItems = [
  { name: "ROLL-OFF TRUCKS", icon: Truck },
  { name: "FRONT LOADERS", icon: Truck },
  { name: "REAR LOADERS", icon: Truck },
  { name: "COMPACTORS", icon: Container },
  { name: "BALERS", icon: Package },
  { name: "RECYCLING", icon: Recycle },
  { name: "GRAPPLE TRUCKS", icon: Construction },
  { name: "HOOK LIFTS", icon: Truck },
  { name: "TRANSFER TRUCKS", icon: Truck },
];

const HeroSection = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const taglineRef = useRef(null);
  const buttonsRef = useRef(null);
  const button1Ref = useRef(null);
  const button2Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero text animations
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      
      tl.fromTo(titleRef.current, 
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 }
      )
      .fromTo(subtitleRef.current,
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        '-=0.7'
      )
      .fromTo(taglineRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.5'
      )
      .fromTo(buttonsRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.3'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Button hover animations
  useEffect(() => {
    const buttons = [button1Ref.current, button2Ref.current];
    
    buttons.forEach(button => {
      if (!button) return;
      
      const handleEnter = () => gsap.to(button, { scale: 1.05, duration: 0.3, ease: 'power2.out' });
      const handleLeave = () => gsap.to(button, { scale: 1, duration: 0.3, ease: 'power2.out' });
      const handleDown = () => gsap.to(button, { scale: 0.95, duration: 0.1 });
      const handleUp = () => gsap.to(button, { scale: 1.05, duration: 0.1 });

      button.addEventListener('mouseenter', handleEnter);
      button.addEventListener('mouseleave', handleLeave);
      button.addEventListener('mousedown', handleDown);
      button.addEventListener('mouseup', handleUp);

      return () => {
        button.removeEventListener('mouseenter', handleEnter);
        button.removeEventListener('mouseleave', handleLeave);
        button.removeEventListener('mousedown', handleDown);
        button.removeEventListener('mouseup', handleUp);
      };
    });
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const marqueeItems = [...equipmentItems, ...equipmentItems, ...equipmentItems, ...equipmentItems];

  return (
    <section ref={heroRef} className="relative min-h-screen bg-[#0A1628] overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center md:bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_green-funding-bros/artifacts/xh1uc1mn_Hero%20image.jpg')`,
          backgroundPosition: 'center center',
        }}
      >
        <div className="absolute inset-0 bg-[#0A1628]/60 md:bg-[#0A1628]/75"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 pt-20">
        <div className="text-center max-w-4xl mx-auto">
          <h1 ref={titleRef} className="text-7xl md:text-9xl lg:text-[10rem] font-black text-white leading-none mb-4" style={{ textShadow: '2px 2px 15px rgba(0, 0, 0, 0.7)' }}>
            WE DUMP
          </h1>
          <h1 ref={subtitleRef} className="text-7xl md:text-9xl lg:text-[10rem] font-black text-[#00A86B] leading-none mb-8" style={{ textShadow: '2px 2px 15px rgba(0, 0, 0, 0.7)' }}>
            CAPITAL
          </h1>
          <p ref={taglineRef} className="text-lg md:text-xl text-white mb-8 max-w-2xl mx-auto" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.6)' }}>
            Dirty trucks make clean money. We're here to make sure your fleet stays full and your business stays funded.
          </p>
          
          {/* CTA Buttons */}
          <div ref={buttonsRef} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button 
              ref={button1Ref}
              onClick={() => scrollToSection('cta')}
              className="bg-[#00A86B] hover:bg-[#008f5b] text-white font-bold px-8 py-4 text-lg rounded-full"
            >
              GET FUNDED NOW
            </Button>
            <Button 
              ref={button2Ref}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] font-bold px-8 py-4 text-lg rounded-full bg-transparent"
            >
              <Phone className="w-5 h-5 mr-2" />
              CALL 1-800-WASTEBRO
            </Button>
          </div>
        </div>
      </div>

      {/* Marquee Equipment Types */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0A1628] py-5 overflow-hidden border-t border-[#1a2d47]">
        <div className="marquee-fast">
          <div className="marquee-content-fast">
            {marqueeItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <React.Fragment key={index}>
                  <div className="flex items-center gap-3 mx-2">
                    <IconComponent className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                    <span className="text-gray-400 font-bold text-sm md:text-base tracking-wider whitespace-nowrap uppercase">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-[#C2A23A] font-bold text-xl mx-3">/</span>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .marquee-fast {
          overflow: hidden;
          width: 100%;
        }
        .marquee-content-fast {
          display: flex;
          align-items: center;
          animation: marquee-fast 20s linear infinite;
        }
        @keyframes marquee-fast {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
